import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  Radio,
  TableBody,
  TableHead,
  TableRow,
  useTheme,
  TableContainer,
  Checkbox,
} from '@mui/material';
import {
  StyledTableCellTwo,
  StyledTableCellWithoutLeftTwo,
  StyledTableCellWithoutRightTwo,
  StyledTableCellWithoutLeftRightTwo,
} from 'routes/Main/style';

import { IFormValues, IFormWatch, IFormRegister } from 'routes/Main/type';

const radioId = [
  'age',
  'fall',
  'evacuation',
  'drug',
  'device',
  'mobility',
  'cognition',
];
const contentLabel = [
  {
    id: 1,
    ko: '나이 (단일 선택)',
    desc: ['아래 사항 해당 없음', '60-69세', '70-79세', '80세 이상'],
  },
  {
    id: 2,
    ko: '낙상 병력 (단일 선택)',
    desc: ['아래 사항 해당 없음', '입원 전 6개월 내에 한 번의 낙상'],
  },
  {
    id: 3,
    ko: '배변, 장과 소변 (단일 선택)',
    desc: ['아래 사항 해당 없음', '실금', '급박 또는 빈번', '급박/빈번과 실금'],
  },
  {
    id: 4,
    ko: '약물 (단일 선택)',
    desc: [
      '아래 사항 해당 없음',
      '1 가지의 고 낙상 위험 약물 (자가통증조절/마약제, 항경련제, 항고혈압제, 이뇨제, 수면제, 완하제, 진정제, 향정신약물)',
      '2 가지 또는 더 많은 고 낙상 위험 약물 (자가통증조절/마약제, 항경련제, 항고혈압제, 이뇨제, 수면제, 완하제, 진정제, 향정신약물)',
      '지난 24시간 내 진정제를 투여받은 시술',
    ],
  },
  {
    id: 5,
    ko: '환자 간호 장치: 환자 묶는 모든 장치\n(단일 선택)',
    desc: [
      '아래 사항 해당 없음',
      '1 가지가 존재한다. (경정맥 주입, 흉관, 유치도뇨관, 순차 압박 장치, 외)',
      '2 가지가 존재한다. (경정맥 주입, 흉관, 유치도뇨관, 순차 압박 장치, 외)',
      '3 가지 또는 더 존재한다. (경정맥 주입, 흉관, 유치도뇨관, 순차 압박 장치, 외)',
    ],
  },
  {
    id: 6,
    ko: '기동성 (중복 선택)',
    desc: [
      '기동성, 이송, 또는 보행을 위한 조력 또는 감독을 요구한다',
      '불안정한 걸음',
      '기동성에 영향을 주는 시각 또는 청각 손상',
    ],
  },
  {
    id: 7,
    ko: '인지 (중복 선택)',
    desc: [
      '당면한 신체 환경으로 변경된 의식',
      '충동적',
      '환자의 신체와 인지 제한에 대한 이해의 부족',
    ],
  },
];
const scoreLabel = [
  { score: '6-13점 :', label: '중등도 낙상위험' },
  { score: '13점 이상 :', label: '고 낙상 위험' },
];

interface Props extends IFormValues, IFormWatch, IFormRegister {
  disabled?: boolean;
}
const FallThreeContents = (props: Props) => {
  const { palette } = useTheme();

  const { disabled, setValue, getValues, register } = props;

  const [sumValue, setSumValue] = useState(0);

  const [mobilityCheckedList, setMobilityCheckedList] = useState<Array<number>>(
    []
  );

  const mobilityOnCheckedItem = useCallback(
    (checked: boolean, item: number) => {
      if (checked) {
        setMobilityCheckedList(prev => [...prev, item]);
      } else if (!checked) {
        setMobilityCheckedList(
          mobilityCheckedList.filter((el, i) => el !== item)
        );
      }
    },
    [mobilityCheckedList]
  );

  const mobilitySumChecked =
    [
      getValues('mobility00'),
      getValues('mobility01'),
      getValues('mobility02'),
    ].filter(a => a).length * 2;

  const [cognitionCheckedList, setCognitionCheckedList] = useState<
    Array<number>
  >([]);

  const cognitionOnCheckedItem = useCallback(
    (checked: boolean, item: number) => {
      if (checked) {
        setCognitionCheckedList(prev => [...prev, item]);
      } else if (!checked) {
        setCognitionCheckedList(
          cognitionCheckedList.filter((el, i) => el !== item)
        );
      }
    },
    [cognitionCheckedList]
  );

  const cognitionSumChecked = [
    getValues('recognition00') ? 1 : 0,
    getValues('recognition01') ? 2 : 0,
    getValues('recognition02') ? 4 : 0,
  ].reduce((a, cur) => {
    if (cur === undefined) return 0;
    return cur ? a + cur : a;
  }, 0);

  const calculateSumValue = () => {
    setSumValue(
      radioId.reduce((acc, cur) => {
        if (cur === 'fall') {
          const value = Number(getValues(cur)) * 5;
          return value ? acc + value : acc;
        } else if (cur === 'evacuation') {
          if (Number(getValues(cur)) === 1 || Number(getValues(cur)) === 3) {
            const value = Number(getValues(cur)) + 1;
            return value ? acc + value : acc;
          }
        } else if (cur === 'drug' && Number(getValues(cur)) > 0) {
          const value = Number(getValues(cur)) * 2 + 1;
          return value ? acc + value : acc;
        }
        const value = Number(getValues(cur));
        return value ? acc + value : acc;
      }, 0)
    );
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setValue(e.target.name, e.target.value);
    calculateSumValue();
  };

  useEffect(() => {
    calculateSumValue();
  }, []);

  return (
    <>
      {/* <SectionTitle title="GCS (Glasgow Coma Scale)" /> */}
      <Box sx={{ width: '98%', margin: '20px' }}>
        <TableContainer>
          <TableHead>
            <TableRow>
              <StyledTableCellTwo
                sx={{
                  padding: '16px',
                  textAlign: 'center',
                }}
              >
                범주
              </StyledTableCellTwo>
              <StyledTableCellWithoutLeftTwo
                sx={{ width: '50px' }}
              ></StyledTableCellWithoutLeftTwo>
              <StyledTableCellWithoutLeftTwo
                sx={{ padding: '16px', paddingLeft: '103px' }}
              >
                점수
              </StyledTableCellWithoutLeftTwo>
              <StyledTableCellTwo></StyledTableCellTwo>
            </TableRow>
          </TableHead>

          <TableBody>
            {contentLabel.map(
              (content: { id: number; ko: string; desc: string[] }, i) => {
                if (i === 5 || i === 6) {
                  return (
                    <TableRow>
                      <StyledTableCellTwo
                        align="center"
                        sx={{
                          padding: '16px',
                          width: '300px',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {content.ko}
                      </StyledTableCellTwo>
                      <StyledTableCellWithoutRightTwo>
                        {content.desc.map((item, i) => {
                          if (content.id === 7) {
                            if (
                              item ===
                              '환자의 신체와 인지 제한에 대한 이해의 부족'
                            ) {
                              return (
                                <TableRow
                                  sx={{ height: '44px', lineHeight: '44px' }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: '50px',
                                      textAlign: 'center',
                                    }}
                                  >
                                    4점
                                  </Box>
                                </TableRow>
                              );
                            }
                            return (
                              <TableRow
                                sx={{ height: '44px', lineHeight: '44px' }}
                              >
                                <Box
                                  sx={{ minWidth: '50px', textAlign: 'center' }}
                                >
                                  {i + 1}점
                                </Box>
                              </TableRow>
                            );
                          }
                          return (
                            <TableRow
                              sx={{ height: '44px', lineHeight: '44px' }}
                            >
                              <Box
                                sx={{ minWidth: '50px', textAlign: 'center' }}
                              >
                                2점
                              </Box>
                            </TableRow>
                          );
                        })}
                      </StyledTableCellWithoutRightTwo>
                      <StyledTableCellWithoutLeftRightTwo>
                        {content.desc.map((v, i) => (
                          <TableRow
                            sx={{
                              height: '44px',
                              lineHeight: '44px',
                            }}
                          >
                            <Box
                              sx={{
                                paddingLeft: '100px',
                                minWidth: '900px',
                              }}
                            >
                              {v}
                            </Box>
                          </TableRow>
                        ))}
                      </StyledTableCellWithoutLeftRightTwo>
                      <StyledTableCellWithoutLeftTwo>
                        {content.desc.map((item, i) => (
                          <TableRow
                            sx={{
                              height: '44px',
                              lineHeight: '44px',
                              textAlign: 'center',
                            }}
                          >
                            <Box
                              sx={{
                                width: '200px',
                                paddingLeft: '50px',
                                paddingRight: '70px',
                              }}
                            >
                              <Checkbox
                                disabled={disabled}
                                defaultChecked={
                                  content.id === 6
                                    ? getValues(`mobility0${i}`)
                                    : getValues(`recognition0${i}`)
                                }
                                {...(register(
                                  content.id === 6
                                    ? `mobility0${i}`
                                    : `recognition0${i}`
                                ),
                                {
                                  onChange: (e, checked) => {
                                    if (content.id === 6) {
                                      setValue(`mobility0${i}`, checked);
                                      mobilityOnCheckedItem(
                                        e.target.checked,
                                        i
                                      );
                                    } else if (content.id === 7) {
                                      setValue(`recognition0${i}`, checked);
                                      cognitionOnCheckedItem(
                                        e.target.checked,
                                        i
                                      );
                                    }
                                  },
                                })}
                              />
                            </Box>
                          </TableRow>
                        ))}
                      </StyledTableCellWithoutLeftTwo>
                    </TableRow>
                  );
                }

                return (
                  <TableRow>
                    <StyledTableCellTwo
                      align="center"
                      sx={{
                        padding: '16px',
                        width: '300px',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {content.ko}
                    </StyledTableCellTwo>
                    <StyledTableCellWithoutRightTwo>
                      {content.desc.map((item, i) => {
                        if (content.id === 2) {
                          return (
                            <TableRow
                              sx={{ height: '44px', lineHeight: '44px' }}
                            >
                              <Box
                                sx={{ minWidth: '50px', textAlign: 'center' }}
                              >
                                {i * 5}점
                              </Box>
                            </TableRow>
                          );
                        } else if (
                          item === '실금' ||
                          item === '급박 또는 빈번'
                        ) {
                          return (
                            <TableRow
                              sx={{ height: '44px', lineHeight: '44px' }}
                            >
                              <Box
                                sx={{ minWidth: '50px', textAlign: 'center' }}
                              >
                                {2}점
                              </Box>
                            </TableRow>
                          );
                        } else if (item === '급박/빈번과 실금') {
                          return (
                            <TableRow
                              sx={{ height: '44px', lineHeight: '44px' }}
                            >
                              <Box
                                sx={{ minWidth: '50px', textAlign: 'center' }}
                              >
                                {4}점
                              </Box>
                            </TableRow>
                          );
                        } else if (content.id === 4 && i > 0) {
                          return (
                            <TableRow
                              sx={{ height: '44px', lineHeight: '44px' }}
                            >
                              <Box
                                sx={{ minWidth: '50px', textAlign: 'center' }}
                              >
                                {i * 2 + 1}점
                              </Box>
                            </TableRow>
                          );
                        }
                        return (
                          <TableRow sx={{ height: '44px', lineHeight: '44px' }}>
                            <Box sx={{ minWidth: '50px', textAlign: 'center' }}>
                              {i}점
                            </Box>
                          </TableRow>
                        );
                      })}
                    </StyledTableCellWithoutRightTwo>
                    <StyledTableCellWithoutLeftRightTwo>
                      {content.desc.map((v, i) => (
                        <TableRow
                          sx={{
                            height: '44px',
                            lineHeight: '44px',
                          }}
                        >
                          <Box sx={{ paddingLeft: '100px', minWidth: '500px' }}>
                            {v}
                          </Box>
                        </TableRow>
                      ))}
                    </StyledTableCellWithoutLeftRightTwo>
                    <StyledTableCellWithoutLeftTwo>
                      <RadioGroup
                        name={radioId[content.id - 1]}
                        defaultValue={Number(
                          getValues(radioId[content.id - 1])
                        )}
                      >
                        {content.desc.map((item, i) => {
                          return (
                            <TableRow
                              sx={{
                                height: '44px',
                                lineHeight: '44px',
                                textAlign: 'center',
                              }}
                            >
                              <Box
                                sx={{
                                  width: '200px',
                                  paddingLeft: '50px',
                                  paddingRight: '70px',
                                }}
                              >
                                <Radio
                                  disabled={disabled}
                                  name={radioId[content.id - 1]}
                                  value={i}
                                  onChange={handleChange}
                                />
                              </Box>
                            </TableRow>
                          );
                        })}
                      </RadioGroup>
                    </StyledTableCellWithoutLeftTwo>
                  </TableRow>
                );
              }
            )}
          </TableBody>
        </TableContainer>
        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems={'flex-end'}
          sx={{ marginTop: '20px' }}
        >
          <Typography
            gutterBottom
            minWidth={115}
            fontWeight={700}
            variant="subtitle1"
          >
            합계: {sumValue + mobilitySumChecked + cognitionSumChecked}점
          </Typography>
          <Typography
            minWidth={115}
            variant="caption"
            sx={{ color: `${palette.primary.main}` }}
          >
            {scoreLabel.map(({ score, label }) => (
              <Typography variant="inherit">
                <Box component={'strong'} mr={0.5}>
                  {score}
                </Box>
                {label}
              </Typography>
            ))}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default FallThreeContents;
